// ExperimentMachineListPageHeader.js
import React from "react";
import { Box, LinearProgress, Typography, Tooltip, Button } from "@mui/material";
import CloudIcon from "@mui/icons-material/Cloud";
import BatteryStdIcon from "@mui/icons-material/BatteryStd";

/** 배터리 색상 헬퍼 */
function colorFor(level) {
  if (level == null) return "#9ca3af"; // gray
  if (level < 20)   return "#ef4444";  // red
  if (level < 50)   return "#f59e0b";  // amber
  return "#10b981";                     // green
}

/** 화면 꺼짐 방지 훅: Screen Wake Lock + NoSleep.js 폴백 */
function useScreenAwake() {
  const [keepAwake, setKeepAwake] = React.useState(false);
  const [error, setError] = React.useState(null);
  const wakeLockRef = React.useRef(null);
  const noSleepRef  = React.useRef(null);

  const loadNoSleep = React.useCallback(async () => {
    if (noSleepRef.current) return noSleepRef.current;
    if (window.NoSleep) {
      noSleepRef.current = new window.NoSleep();
      return noSleepRef.current;
    }
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/nosleep.js@0.12.0/dist/NoSleep.min.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    noSleepRef.current = new window.NoSleep();
    return noSleepRef.current;
  }, []);

  const enableFallback = React.useCallback(async () => {
    try {
      const ns = await loadNoSleep();
      await ns.enable(); // 음소거된 초소형 비디오 루프
    } catch (e) {
      setError("NoSleep 활성화 실패");
    }
  }, [loadNoSleep]);

  const requestWakeLock = React.useCallback(async () => {
    try {
      setError(null);
      if ("wakeLock" in navigator) {
        const sentinel = await navigator.wakeLock.request("screen");
        wakeLockRef.current = sentinel;
        // 시스템이 해제하면 자동 재요청
        sentinel.addEventListener("release", () => {
          wakeLockRef.current = null;
          if (keepAwake && document.visibilityState === "visible") {
            requestWakeLock();
          }
        });
      } else {
        await enableFallback();
      }
    } catch (e) {
      setError(e?.name || String(e));
      await enableFallback();
    }
  }, [keepAwake, enableFallback]);

  const release = React.useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    } catch {}
  }, []);

  const toggle = React.useCallback(async () => {
    if (keepAwake) {
      setKeepAwake(false);
      await release();
    } else {
      setKeepAwake(true);
      await requestWakeLock(); // 버튼 클릭 직후 호출
    }
  }, [keepAwake, release, requestWakeLock]);

  // 탭이 다시 보이면 필요 시 재요청
  React.useEffect(() => {
    const reapply = () => {
      if (keepAwake && document.visibilityState === "visible" && !wakeLockRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", reapply);
    window.addEventListener("focus", reapply);
    return () => {
      document.removeEventListener("visibilitychange", reapply);
      window.removeEventListener("focus", reapply);
    };
  }, [keepAwake, requestWakeLock]);

  // 언마운트 시 정리
  React.useEffect(() => () => { release(); }, [release]);

  React.useEffect(() => {
    if (!keepAwake) return;
    const t = setTimeout(() => { /* 33분 후 자동 끄기 */ toggle(); }, 33 * 60 * 1000);
    return () => clearTimeout(t);
  }, [keepAwake, toggle]);

  return { keepAwake, toggle, error };
}

function ExperimentMachineListPageHeader({ battery }) {
  const pct = typeof battery === "number" ? battery : 0;
  const barColor = colorFor(battery);
  const { keepAwake, toggle, error } = useScreenAwake();

  console.log("Battery level : ", battery);
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" }, // 모바일: 세로, 데스크탑: 가로
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: { xs: "flex-start", md: "space-between" },
        gap: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 6 },
        py: { xs: 1.5, md: 2 },
        boxSizing: "border-box",
      }}
    >
      {/* 왼쪽: 아이콘 + 타이틀 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CloudIcon
          sx={{
            color: "#2877b9",
          }}
        />
        <Typography
          variant="h6"
          component="h2"
          sx={{
            color: "white",
            fontFamily: "GmarketSansMedium",
            m: 0,
          }}
        >
          자극 (Stimulation)
        </Typography>
      </Box>

      {/* 오른쪽: 배터리 + 화면 켜두기 버튼 (줄바꿈 가능 영역) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",              // 좁을 때 줄바꿈
          gap: 1.5,
          mt: { xs: 1, md: 0 },
        }}
      >
        {/* Battery status */}
        <Tooltip title="Battery level">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              background: "#1f2937",
            }}
          >
            <BatteryStdIcon sx={{ color: barColor }} />
            <Typography
              variant="body2"
              sx={{ color: "white", minWidth: 34, textAlign: "right" }}
            >
              {battery == null ? "—" : `${pct}%`}
            </Typography>
            <Box sx={{ width: 90 }}>
              <LinearProgress
                variant={battery == null ? "indeterminate" : "determinate"}
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 4,
                  backgroundColor: "#374151",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: barColor,
                  },
                }}
              />
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default ExperimentMachineListPageHeader;